// #/help/recording-tips — static content page, no auth, no DB.
// Copy is verbatim from _claude_code_prompts/08_recording_tips_help_page.md.

export function renderRecordingTips(container: HTMLElement): void {
  container.innerHTML = `
    <main class="container help-page">
      <a href="#/" class="back-link muted small">← Back to PlanIt onboarding</a>
      <article class="prose">
        <h1>Recording videos providers love to upload</h1>
        <p>We accept any video your phone can record — but a 10-second clip of your
        espresso machine doing what it does will earn you more bookings than a perfect
        4K production. Here's how to make it work.</p>

        <h2>The three rules</h2>
        <ol>
          <li><strong>Hold the phone sideways (horizontal).</strong> The PlanIt app shows
          videos in landscape mode. Vertical phone videos appear with black bars on the
          sides — they still play, but waste space.</li>
          <li><strong>Keep it short — aim for 15 to 60 seconds.</strong> Tourists watch on
          phones, often offline. Longer clips eat their data plan and rarely get watched to
          the end. Show the best bit; cut the rest.</li>
          <li><strong>Use a tripod, table, or a steady wall.</strong> Handheld shake makes
          the viewer slightly seasick. If you don't own a tripod, prop the phone against a
          sugar pot. Seriously, that works.</li>
        </ol>

        <h2>What to show</h2>
        <p>Tourists want to <em>see</em> what they'll experience. Cinematic camerawork is
        not required. These work:</p>
        <ul>
          <li>The dining room at dinner service, lights low, candles on</li>
          <li>Your barman pulling an espresso with the morning sun coming through the door</li>
          <li>The bike workshop floor — tools, a half-assembled wheel, the smell you can't
          film but the shop tells</li>
          <li>A pizzaiolo working dough; a sommelier opening a bottle; a chef plating</li>
          <li>Wide shot of the space, slow pan, no music</li>
        </ul>
        <p>What doesn't work as well: you talking to the camera explaining things. Tourists
        scroll past those.</p>

        <h2>iPhone users — one camera setting to change</h2>
        <p>iPhones record in a format called <strong>HEVC</strong> by default. It's
        smaller-file and great quality, but some older Android phones don't play it back
        smoothly inside the PlanIt app.</p>
        <p>To switch to the universally-compatible format:</p>
        <p><strong>Settings → Camera → Formats → choose "Most Compatible"</strong></p>
        <p>That changes recording to H.264 (the universal MP4 format). Your file sizes go up
        by about 30%, but every phone on the planet plays it back without stuttering.</p>
        <p>(Already recorded the perfect clip in HEVC? Upload it anyway. It'll work on
        iPhone-using tourists, and most Android viewers too — just not all.)</p>

        <h2>Android users — a quick check</h2>
        <ul>
          <li>Open the camera, switch to video</li>
          <li>Tap the settings cog</li>
          <li>Resolution: 1080p at 30fps is the sweet spot (4K is overkill for phone
          playback and eats your upload time)</li>
          <li>Format: MP4 (it usually is by default)</li>
        </ul>

        <h2>File size — there's no hard limit</h2>
        <p>Our system handles videos of any size. But upload time is roughly your file size
        divided by your home wifi upload speed. A 1GB video on a typical 20 Mbps upload
        takes about 7 minutes. Stay near the page until it's done.</p>

        <h2>Lighting</h2>
        <p>If the place is dim (a wine bar, a candlelit trattoria), don't try to fix it in
        software — phone "low light mode" looks worse than honest shadow. Embrace the mood.
        Tourists looking for that vibe are <em>more</em> likely to book if it looks like the
        place they imagined.</p>

        <h2>Audio</h2>
        <p>Phone microphones are fine for ambient sound. Wind ruins everything outdoors — if
        it's windy, position the phone behind a wall or in a corner. Indoors, background
        chatter and clinking glasses are good — that's the soundtrack of "this place is alive".</p>

        <h2>Common mistakes</h2>
        <ul>
          <li><strong>Filming through glass</strong> — windows reflect; cases of pastries
          lose detail. Open the case, get inside the window.</li>
          <li><strong>Pointing at a light source</strong> — silhouettes the subject. Light
          should be behind <em>you</em>, not behind the subject.</li>
          <li><strong>The 30-second narration of why you opened the business</strong> — save
          it for your description; show, don't tell.</li>
          <li><strong>Filming sideways then rotating the phone mid-shot</strong> — the video
          saves in the orientation you started. Decide before you press record.</li>
        </ul>

        <h2>A tiny script for "I genuinely don't know what to film"</h2>
        <p>If you're stuck, try this 45-second skeleton:</p>
        <ul>
          <li>0:00–0:10 — wide shot of the space, no movement</li>
          <li>0:10–0:25 — close shot of the thing you do best (the pour, the plating, the
          wheel-true)</li>
          <li>0:25–0:40 — a satisfied customer's face / hands / the result on the table</li>
          <li>0:40–0:45 — wide shot again, fade</li>
        </ul>
        <p>You don't need to be a filmmaker. The point is "this is real, here's the proof".</p>

        <p class="muted small">That's the whole playbook. Upload what you can — we can always
        swap it later. Questions: <a href="mailto:john@ebikesflorence.com">john@ebikesflorence.com</a>.</p>
      </article>
    </main>
  `;
}
